/**
 * SupportDashboardPage — admin overview at /support.
 *
 * Shows ticket counts, the moderation queue, and a sticky filter bar. The
 * queue mirrors the FeedbackListPage UX so admins navigate them similarly.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Search, AlertCircle, MessageSquare, LifeBuoy, FileText } from 'lucide-react';
import { cn } from '@/src/infrastructure/lib/utils';
import {
  getSupportStats,
  listSupportTickets,
  type AdminSupportTicket,
  type AdminSupportTicketCategory,
  type AdminSupportTicketPriority,
  type AdminSupportTicketStatus,
  type SupportStats,
} from '@/src/infrastructure/lib/api';
import {
  PRIORITY_TONE,
  STATUS_LABEL,
  STATUS_TONE,
  SUPPORT_CATEGORIES,
  SUPPORT_PRIORITIES,
  SUPPORT_STATUSES,
} from './constants';

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export default function SupportDashboardPage() {
  const [items, setItems] = useState<AdminSupportTicket[]>([]);
  const [stats, setStats] = useState<SupportStats | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<AdminSupportTicketStatus | ''>('');
  const [category, setCategory] = useState<AdminSupportTicketCategory | ''>('');
  const [priority, setPriority] = useState<AdminSupportTicketPriority | ''>('');

  useEffect(() => {
    void getSupportStats().then(setStats).catch(() => undefined);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => void load(1, false), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, category, priority]);

  async function load(p: number, append: boolean) {
    setLoading(true);
    setError(null);
    try {
      const res = await listSupportTickets({
        page: p,
        limit: 20,
        search: search || undefined,
        status: status || undefined,
        category: category || undefined,
        priority: priority || undefined,
      });
      setItems((prev) => (append ? [...prev, ...res.items] : res.items));
      setPage(res.meta.page);
      setHasMore(res.meta.page < res.meta.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load tickets');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="p-6 md:p-8 space-y-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl md:text-3xl font-display font-bold text-on-surface">
          Support tickets
        </h1>
        <p className="text-sm text-on-surface-variant">
          Moderation queue for user-submitted help &amp; support requests.
        </p>
      </header>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={<MessageSquare className="w-5 h-5" />}
          label="Total"
          value={stats?.total ?? 0}
        />
        <StatCard
          icon={<LifeBuoy className="w-5 h-5 text-amber-600" />}
          label="Open"
          value={stats?.openCount ?? 0}
        />
        <StatCard
          icon={<AlertCircle className="w-5 h-5 text-rose-600" />}
          label="Unassigned open"
          value={stats?.unassignedOpen ?? 0}
        />
        <StatCard
          icon={<FileText className="w-5 h-5 text-sky-600" />}
          label="In progress"
          value={stats?.byStatus?.IN_PROGRESS ?? 0}
        />
      </div>

      <div className="flex items-center justify-end">
        <Link
          to="/support/faqs"
          className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-white shadow-sm hover:opacity-90"
        >
          <FileText className="w-4 h-4" /> Manage FAQs
        </Link>
      </div>

      {/* Filter bar */}
      <div className="sticky top-0 z-10 -mx-6 md:-mx-8 px-6 md:px-8 py-3 bg-surface-container-lowest/80 backdrop-blur-md border-b border-border-subtle flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[12rem]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-on-surface-variant" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search subject or description"
            className="w-full rounded-full border border-border-subtle bg-surface px-9 py-1.5 text-xs"
          />
        </div>
        <SelectFilter
          value={status}
          onChange={(v) => setStatus(v as AdminSupportTicketStatus | '')}
          options={SUPPORT_STATUSES}
          allLabel="All statuses"
          formatLabel={(s) => STATUS_LABEL[s as AdminSupportTicketStatus]}
        />
        <SelectFilter
          value={category}
          onChange={(v) => setCategory(v as AdminSupportTicketCategory | '')}
          options={SUPPORT_CATEGORIES}
          allLabel="All categories"
        />
        <SelectFilter
          value={priority}
          onChange={(v) => setPriority(v as AdminSupportTicketPriority | '')}
          options={SUPPORT_PRIORITIES}
          allLabel="All priorities"
        />
      </div>

      {error && (
        <div className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl border border-border-subtle overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-low/40 text-[10px] uppercase tracking-wider text-on-surface-variant">
            <tr>
              <th className="text-left py-2 px-3">Subject</th>
              <th className="text-left py-2 px-3 hidden md:table-cell">User</th>
              <th className="text-left py-2 px-3 hidden md:table-cell">Category</th>
              <th className="text-left py-2 px-3">Status</th>
              <th className="text-left py-2 px-3">Priority</th>
              <th className="text-left py-2 px-3 hidden lg:table-cell">Created</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="text-center text-on-surface-variant py-12">
                  No tickets match these filters.
                </td>
              </tr>
            )}
            {items.map((t) => (
              <motion.tr
                key={t.id}
                initial={{ opacity: 0, y: 2 }}
                animate={{ opacity: 1, y: 0 }}
                className="border-t border-border-subtle hover:bg-surface-low/40"
              >
                <td className="py-2 px-3">
                  <Link
                    to={`/support/${t.id}`}
                    className="font-semibold text-on-surface hover:text-primary"
                  >
                    {t.subject}
                  </Link>
                  <div className="text-[10px] text-on-surface-variant">
                    #{t.id.slice(0, 8)} · {t.messageCount} msgs
                  </div>
                </td>
                <td className="py-2 px-3 hidden md:table-cell text-on-surface-variant text-xs">
                  {t.user?.name ?? t.user?.email ?? '—'}
                </td>
                <td className="py-2 px-3 hidden md:table-cell text-xs uppercase tracking-wider text-on-surface-variant">
                  {t.category}
                </td>
                <td className="py-2 px-3">
                  <span
                    className={cn(
                      'inline-block rounded-full border px-2 py-0.5 text-[10px] font-semibold',
                      STATUS_TONE[t.status],
                    )}
                  >
                    {STATUS_LABEL[t.status]}
                  </span>
                </td>
                <td className={cn('py-2 px-3 text-xs font-semibold uppercase', PRIORITY_TONE[t.priority])}>
                  {t.priority}
                </td>
                <td className="py-2 px-3 hidden lg:table-cell text-[10px] text-on-surface-variant">
                  {formatDate(t.createdAt)}
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => void load(page + 1, true)}
            disabled={loading}
            className="rounded-full border border-border-subtle px-5 py-2 text-xs font-semibold text-on-surface-variant hover:bg-surface-low"
          >
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
}) {
  return (
    <div className="rounded-2xl border border-border-subtle bg-surface-low/40 p-4">
      <div className="flex items-center gap-2 text-on-surface-variant">
        {icon}
        <span className="text-[11px] uppercase tracking-wider">{label}</span>
      </div>
      <p className="mt-2 text-2xl font-display font-bold text-on-surface">{value}</p>
    </div>
  );
}

function SelectFilter({
  value,
  onChange,
  options,
  allLabel,
  formatLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
  allLabel: string;
  formatLabel?: (v: string) => string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-full border border-border-subtle bg-surface px-3 py-1.5 text-xs"
    >
      <option value="">{allLabel}</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {formatLabel ? formatLabel(o) : o}
        </option>
      ))}
    </select>
  );
}
